/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  MoreVert,
  Send,
  Cancel,
  Visibility,
  AccessTime,
  Email,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  InvitationCardProps,
  InvitationStatus,
} from '../../../types/invitation';
import { getRoleDisplayName, getRoleColor } from '../../../utils/permissions';
import InvitationStatusBadge from './InvitationStatusBadge';

/**
 * InvitationCard component displays individual invitation information
 * with status, recipient details, and available actions.
 */
const InvitationCard = ({
  invitation,
  onResend,
  onCancel,
  onView,
}: InvitationCardProps) => {
  const { t } = useTranslation('studio');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleResend = () => {
    handleMenuClose();
    onResend?.(invitation.id);
  };

  const handleCancel = () => {
    handleMenuClose();
    onCancel?.(invitation.id);
  };

  const handleView = () => {
    handleMenuClose();
    onView?.(invitation.id);
  };

  const formatDate = (date: Date) =>
    new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const formatRelativeTime = (date: Date) => {
    const now = new Date();
    const diffInDays = Math.ceil(
      (new Date(date).getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays < 0) {
      return t('invitationCard.expiredDaysAgo', {
        days: Math.abs(diffInDays),
        count: Math.abs(diffInDays),
      });
    }
    if (diffInDays === 0) {
      return t('invitationCard.expiresToday');
    }
    if (diffInDays === 1) {
      return t('invitationCard.expiresTomorrow');
    }
    return t('invitationCard.expiresIn', {
      days: diffInDays,
      count: diffInDays,
    });
  };

  // No handler means the viewer may not take that action
  const isPending = invitation.status === InvitationStatus.PENDING;
  const canResend = isPending && !!onResend;
  const canCancel = isPending && !!onCancel;
  const showExpirationWarning =
    invitation.status === InvitationStatus.PENDING &&
    new Date(invitation.expiresAt).getTime() - new Date().getTime() <
      3 * 24 * 60 * 60 * 1000; // 3 days

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 2,
        },
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 2 }}>
        {/* Header with email and actions */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Email fontSize="small" color="action" />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {invitation.email}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              {t('invitationCard.roleLabel')}{' '}
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: getRoleColor(invitation.role),
                  fontWeight: 500,
                }}
              >
                {getRoleDisplayName(invitation.role)}
              </Typography>
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
            aria-label={t('invitationCard.ariaLabel')}
          >
            <MoreVert />
          </IconButton>
        </Box>

        {/* Status and timing information */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <InvitationStatusBadge
              status={invitation.status}
              expiresAt={invitation.expiresAt}
              size="small"
            />
            {showExpirationWarning && (
              <Tooltip title={formatRelativeTime(invitation.expiresAt)}>
                <AccessTime fontSize="small" color="warning" />
              </Tooltip>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            {t('invitationCard.sentLabel')} {formatDate(invitation.sentAt)}
            {invitation.status === InvitationStatus.PENDING && (
              <>
                {' • '}
                {formatRelativeTime(invitation.expiresAt)}
              </>
            )}
          </Typography>
        </Box>

        {/* Custom message preview */}
        {invitation.message && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom>
              {t('invitationCard.messageLabel')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              &ldquo;{invitation.message}&rdquo;
            </Typography>
          </Box>
        )}

        {/* Invited by information */}
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            {t('invitationCard.invitedBy')}{' '}
            {invitation.firstName && invitation.lastName
              ? `${invitation.firstName} ${invitation.lastName}`
              : invitation.invited_by_name || 'Unknown'}
          </Typography>
        </Box>
      </CardContent>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleView}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('invitationCard.actions.viewDetails')}</ListItemText>
        </MenuItem>

        {canResend && (
          <MenuItem onClick={handleResend}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('invitationCard.actions.resend')}</ListItemText>
          </MenuItem>
        )}

        {canCancel && <Divider />}
        {canCancel && (
          <MenuItem onClick={handleCancel} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Cancel fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>{t('invitationCard.actions.cancel')}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Card>
  );
};

export default InvitationCard;
